import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight, ArrowUpRight, BookOpen, Brain, Check, Compass, Dices, FlaskConical,
  Flame, Layers3, PencilLine, Play, Plus, Sparkles, Target, Timer, X
} from 'lucide-react';
import { Eyebrow, Panel, StatusBadge } from '@cpu/design-system';
import {
  addScratchItem, clearScratchItem, useAllReviewCards, useDueReviewCards,
  useFollowupProgressCount, useFocusMinutesToday, useRecentItems, useRecentTypes,
  useScratchItems, useStreak, useVisitedModuleIds, useXpSeries, useXpToday
} from '@cpu/progress-store';
import { useFocus } from '../app/focusTimer';
import { useToast } from '../app/toast';
import { useComfort } from '../app/comfort';
import { playConfirm, playTick } from '../app/sound';
import type { CurriculumManifest } from '@cpu/domain';
import curriculumJson from '../generated/curriculum.json';

const curriculum = curriculumJson as unknown as CurriculumManifest;

interface Mission {
  eyebrow: string;
  title: string;
  body: string;
  minutes: string;
  kind: 'review' | 'continue' | 'fresh';
  cta: string;
  href: string;
  params?: Record<string, string>;
  icon: 'review' | 'continue' | 'fresh';
}

function buildMission(dueCount: number, recent: Array<{ href: string; title: string }>): Mission {
  if (dueCount > 0) {
    return {
      eyebrow: 'Retrieval first',
      title: `Clear ${dueCount} due ${dueCount === 1 ? 'prompt' : 'prompts'}`,
      body: 'These are facts and invariants you chose to keep. Recalling them now is what makes them permanent — the highest-leverage minutes available today.',
      minutes: `${Math.min(15, Math.max(3, dueCount * 2))} min`,
      kind: 'review',
      cta: 'Start the review session',
      href: '/review',
      icon: 'review'
    };
  }
  const latest = recent[0];
  if (latest) {
    return {
      eyebrow: 'Momentum beats motivation',
      title: `Continue: ${latest.title.length > 58 ? `${latest.title.slice(0, 58)}…` : latest.title}`,
      body: 'Picking up exactly where you stopped costs the least willpower. Your own trail re-anchors the context in under a minute.',
      minutes: '5–20 min',
      kind: 'continue',
      cta: 'Resume where you left off',
      href: latest.href,
      icon: 'continue'
    };
  }
  return {
    eyebrow: 'Begin',
    title: 'Run the flagship trace lab',
    body: 'Sliding windows, operated by hand. Move the pointers, watch the invariant hold, then break it on purpose — understanding by doing, not by scrolling.',
    minutes: '12–15 min',
    kind: 'fresh',
    cta: 'Open the window-invariant lab',
    href: '/learn/m13/window-invariant',
    icon: 'fresh'
  };
}

function greetingFor(hour: number): string {
  if (hour < 5) return 'Still up';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function TodayPage() {
  const due = useDueReviewCards();
  const allCards = useAllReviewCards();
  const recent = useRecentItems(5);
  const recentTypes = useRecentTypes();
  const followupCount = useFollowupProgressCount();
  const streak = useStreak();
  const xpToday = useXpToday();
  const xpSeries = useXpSeries(105);
  const focusMinutes = useFocusMinutesToday();
  const scratch = useScratchItems();
  const visitedModules = useVisitedModuleIds();
  const { openLauncher } = useFocus();
  const { comfort } = useComfort();
  const { toast } = useToast();
  const [scratchDraft, setScratchDraft] = useState('');

  const mission = buildMission(due.length, recent.map(({ href, title }) => ({ href, title })));
  const goalRatio = Math.min(1, xpToday / Math.max(1, comfort.dailyGoalXp));
  const now = new Date();
  const today = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const ritual = buildRitualSteps({
    readAtom: recentTypes.has('atom'),
    ranLab: recentTypes.has('lesson'),
    attempted: followupCount > 0,
    keptCard: allCards.length > 0,
    focused: focusMinutes > 0
  });

  const surprise = () => {
    playTick();
    const pick = curriculum.modules[Math.floor(Math.random() * curriculum.modules.length)]!;
    toast({ tone: 'info', title: `M${pick.number} — ${pick.shortTitle}`, body: 'A random module. Give it ninety seconds; curiosity counts as studying.' });
    window.setTimeout(() => { window.location.assign(`/atlas/${pick.slug}`); }, 350);
  };

  const submitScratch = async () => {
    if (!scratchDraft.trim()) return;
    await addScratchItem(scratchDraft);
    setScratchDraft('');
    playConfirm();
  };

  const clearItem = async (id: number) => {
    await clearScratchItem(id);
    playTick();
  };

  const m13 = curriculum.modules.find((module) => module.id === 'M13')!;
  const pathStops = ['M04', 'M02', 'M08', 'M11', 'M13'];
  const pathModules = pathStops
    .map((id) => curriculum.modules.find((module) => module.id === id))
    .filter((module): module is NonNullable<typeof module> => Boolean(module));

  const MissionIcon = mission.icon === 'review' ? Brain : mission.icon === 'continue' ? BookOpen : FlaskConical;

  return (
    <div className="page page-today">
      {/* ── Masthead ─────────────────────────────────────────────── */}
      <section className="today-masthead">
        <div className="today-masthead-copy">
          <p className="today-date"><span className="pulse-dot" aria-hidden="true" />{today} · learning queue</p>
          <h1>{greetingFor(now.getHours())}.<br />Build the invariant.<br />Then trust the code.</h1>
          <p className="today-lede">This workspace measures what you can <em>explain, trace and apply</em> — never how far you scrolled. Everything below is real, local, and yours.</p>
        </div>
        <div className="corpus-stamp" aria-label="Content corpus summary">
          <span><b>{curriculum.totals.modules}</b><small>modules</small></span>
          <span><b>{curriculum.totals.atoms.toLocaleString()}</b><small>atoms</small></span>
          <span><b>{curriculum.totals.problems}</b><small>problems</small></span>
        </div>
      </section>

      {/* ── The one mission ──────────────────────────────────────── */}
      <section className="mission-band" aria-label="Recommended next step">
        <Link to={mission.href} {...(mission.params ? { params: mission.params } : {})} className={`mission-card mission-${mission.kind}`}>
          <div className="mission-rail" aria-hidden="true">
            <span className="mission-icon"><MissionIcon size={22} /></span>
            <span className="mission-index">01</span>
          </div>
          <div className="mission-copy">
            <div className="mission-meta">
              <StatusBadge>{mission.eyebrow}</StatusBadge>
              <span className="mission-minutes"><Timer size={13} />{mission.minutes}</span>
            </div>
            <h2>{mission.title}</h2>
            <p>{mission.body}</p>
            <span className="text-action">{mission.cta} <ArrowRight size={17} /></span>
          </div>
          <div className="mission-glow" aria-hidden="true" />
        </Link>

        <aside className="session-recipe" aria-label="A good hour">
          <Eyebrow>A good hour, composed</Eyebrow>
          <ol>
            <li>
              <b>Warm-up</b><span>One recall prompt, from memory, before opening anything.</span>
              <Link to="/review" className="recipe-link">Clear one <ArrowUpRight size={13} /></Link>
            </li>
            <li>
              <b>Main block</b><span>One lab or one atom. Timed, single intention.</span>
              <button className="recipe-link" onClick={openLauncher}><Play size={13} />Start timer</button>
            </li>
            <li>
              <b>Cool-down</b><span>One line: what was hard? That line becomes tomorrow's warm-up.</span>
              <Link to="/playbook" className="recipe-link">Open the log <ArrowUpRight size={13} /></Link>
            </li>
          </ol>
        </aside>
      </section>

      {/* ── Bento grid ───────────────────────────────────────────── */}
      <section className="bento" aria-label="Your systems">

        <Panel className="bento-tile tile-reviews">
          <div className="tile-head"><div><Eyebrow>Retrieval</Eyebrow><h2>Reviews due</h2></div><Brain size={20} /></div>
          {due.length ? (
            <>
              <strong className="large-number">{due.length}</strong>
              <p>prompts are ready. Honest recall only — rate truthfully; the schedule adapts to <i>you</i>.</p>
              <Link to="/review" className="text-action">Start review <ArrowRight size={15} /></Link>
            </>
          ) : (
            <div className="honest-empty">
              <b>{allCards.length > 0 ? 'All clear' : 'Nothing yet — by design'}</b>
              <p>{allCards.length > 0
                ? 'No prompts are due right now. Enjoy a guilt-free break; the next one returns exactly on schedule.'
                : 'Save check questions from any atom’s reader and they return here on a spaced schedule. Nothing is fabricated for the dashboard.'}</p>
            </div>
          )}
        </Panel>

        <Panel className="bento-tile tile-focus">
          <div className="tile-head"><div><Eyebrow>Deep work</Eyebrow><h2>Focus blocks</h2></div><Timer size={20} /></div>
          <strong className="large-number">{focusMinutes}<small>min</small></strong>
          <p>logged today. One visible countdown, one intention, a hard stop — the ring does the remembering so you don't have to.</p>
          <div className="tile-actions">
            <button className="btn-primary" onClick={openLauncher}><Play size={15} />New block</button>
            <span className="tile-hint">{comfort.focusPresetMinutes} min preset</span>
          </div>
        </Panel>

        <Panel className="bento-tile tile-ledger">
          <div className="tile-head">
            <div><Eyebrow>Effort ledger</Eyebrow><h2>Consistency</h2></div>
            <span className={`streak-chip ${streak.activeToday ? 'is-live' : ''}`} title="Current streak of active days">
              <Flame size={14} />{streak.current}
            </span>
          </div>
          <div className="ledger-row">
            <div className="goal-ring" role="img" aria-label={`${xpToday} of ${comfort.dailyGoalXp} effort points today`}>
              <svg viewBox="0 0 84 84">
                <circle cx="42" cy="42" r="36" />
                <circle className="ring-fill" cx="42" cy="42" r="36" style={{ '--p': goalRatio } as React.CSSProperties} />
              </svg>
              <div className="goal-ring-label"><b>{xpToday}</b><small>/{comfort.dailyGoalXp}</small></div>
            </div>
            <div className="ledger-copy">
              {goalRatio >= 1
                ? <><b>Daily goal met.</b><p>The ledger only counts real work — reading, recall, attempts, focus minutes. Today's number was earned.</p></>
                : <><b>{Math.round(goalRatio * 100)}% of today's goal.</b><p>{xpToday === 0 ? 'One opened atom or one recalled prompt starts the ring.' : `${Math.max(1, comfort.dailyGoalXp - xpToday)} points to go — one prompt or one atom each.`}</p></>}
            </div>
          </div>
          <Heatmap series={xpSeries} />
          <div className="ledger-foot">
            <span><Flame size={13} />{streak.current} current</span>
            <span><Layers3 size={13} />{streak.longest} longest</span>
            {streak.current === 0 && <em>Day one starts with a single open atom. Never miss twice.</em>}
          </div>
        </Panel>

        <Panel className="bento-tile tile-continue">
          <div className="tile-head"><div><Eyebrow>Continue</Eyebrow><h2>Your trail</h2></div><BookOpen size={20} /></div>
          {recent.length ? (
            <ul className="recent-list">
              {recent.map((item) => (
                <li key={item.href}>
                  <Link to={item.href}>
                    <span className="recent-kind">{item.type}</span>
                    <span className="recent-title">{item.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="honest-empty">
              <b>Your history is empty</b>
              <p>Open any atom below — visited pages stay cached and appear here, even offline.</p>
              <Link to="/atlas/$moduleId" params={{ moduleId: 'm13' }} className="text-action">Start with M13 <ArrowRight size={15} /></Link>
            </div>
          )}
        </Panel>

        <Panel className={`bento-tile tile-ritual ${ritual.allDone ? 'is-complete' : ''}`}>
          <div className="tile-head">
            <div>
              <Eyebrow>{ritual.allDone ? 'Ritual complete' : 'Start here'}</Eyebrow>
              <h2>{ritual.allDone ? 'The whole loop, run once' : 'Learn the loop by doing it'}</h2>
            </div>
            {ritual.allDone ? <Check size={20} /> : <Target size={20} />}
          </div>
          {!ritual.allDone && <p className="ritual-lede">Five moves, in order. Each is the smallest version of a habit worth keeping.</p>}
          <ol className="ritual-list">
            {ritual.steps.map((step, index) => (
              <li key={step.id} className={step.done ? 'is-done' : ''}>
                <span className="ritual-check" aria-hidden="true">{step.done ? <Check size={13} /> : String(index + 1).padStart(2, '0')}</span>
                <span className="ritual-copy"><b>{step.title}</b><small>{step.hint}</small></span>
                {!step.done && step.href && <Link to={step.href} className="ritual-link">Do it <ArrowUpRight size={13} /></Link>}
              </li>
            ))}
          </ol>
        </Panel>

        <Panel className="bento-tile tile-scratch">
          <div className="tile-head"><div><Eyebrow>Park it here</Eyebrow><h2>Scratch pad</h2></div><PencilLine size={20} /></div>
          <p className="tile-note">A stray thought pulling you away? Drop it here and return to the work. Emptying the cache frees the RAM.</p>
          <form className="scratch-form" onSubmit={(event) => { event.preventDefault(); void submitScratch(); }}>
            <input
              value={scratchDraft}
              onChange={(event) => setScratchDraft(event.target.value)}
              placeholder="e.g. look up how judges measure runtime…"
              aria-label="New scratch note"
              maxLength={140}
            />
            <button type="submit" aria-label="Park this thought" disabled={!scratchDraft.trim()}><Plus size={16} /></button>
          </form>
          {scratch.length > 0 && (
            <ul className="scratch-list">
              {scratch.slice(0, 5).map((item) => (
                <li key={item.id}>
                  <span>{item.body}</span>
                  <button onClick={() => void clearItem(item.id!)} aria-label="Done — clear this note"><X size={13} /></button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel className="bento-tile tile-serendipity">
          <div className="tile-head"><div><Eyebrow>Serendipity engine</Eyebrow><h2>Surprise me</h2></div><Dices size={20} /></div>
          <p>Structured practice builds mastery; wandering builds the map that connects it. One tap drops you into a random module — give it ninety seconds.</p>
          <button className="btn-ghost" onClick={surprise}><Sparkles size={15} />Teleport me</button>
          {visitedModules.size > 0 && <small className="tile-hint">{visitedModules.size} of {curriculum.totals.modules} modules touched so far</small>}
        </Panel>
      </section>

      {/* ── Path + corpus ────────────────────────────────────────── */}
      <section className="path-strip">
        <div><Eyebrow>Current path</Eyebrow><h2>Prerequisites for M13</h2></div>
        <div className="path-nodes" style={{ '--path-count': pathModules.length } as React.CSSProperties}>
          {pathModules.map((module, index) => (
            <div className={index === pathModules.length - 1 ? 'current' : ''} key={module.id}>
              <span>{index < pathModules.length - 1 ? '→' : '★'}</span>
              <Link to="/atlas/$moduleId" params={{ moduleId: module.slug }}><b>{module.id}</b>&nbsp;{module.shortTitle.length > 26 ? `${module.shortTitle.slice(0, 26)}…` : module.shortTitle}</Link>
            </div>
          ))}
        </div>
        <Link to="/atlas" className="icon-text-link"><Compass size={17} />Open atlas</Link>
      </section>

      <section className="corpus-note">
        <Layers3 size={22} />
        <div><b>{m13.atomCount} M13 atoms are available as reference.</b><p>The Trace Lab is a curated learning layer over that corpus; it does not replace the complete material.</p></div>
        <Link to="/atlas/$moduleId" params={{ moduleId: 'm13' }}>Inspect module</Link>
      </section>
    </div>
  );
}

/* ── Heatmap ─────────────────────────────────────────────────────────── */

function Heatmap({ series }: { series: Map<string, number> }) {
  const weeks = useMemo(() => {
    const cells: Array<{ day: string; value: number }> = [];
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 104);
    start.setDate(start.getDate() - start.getDay()); // align columns to Sunday
    for (const cursor = new Date(start); cursor <= today; cursor.setDate(cursor.getDate() + 1)) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
      cells.push({ day: key, value: series.get(key) ?? 0 });
    }
    const columns: Array<typeof cells> = [];
    for (let index = 0; index < cells.length; index += 7) columns.push(cells.slice(index, index + 7));
    return columns;
  }, [series]);

  const max = Math.max(12, ...series.values());
  const level = (value: number): number => value === 0 ? 0 : value < max * 0.25 ? 1 : value < max * 0.5 ? 2 : value < max * 0.75 ? 3 : 4;

  return (
    <div className="heatmap" role="img" aria-label="Effort over the last fifteen weeks; darker cells mean more real work">
      {weeks.map((column, columnIndex) => (
        <div className="heatmap-col" key={columnIndex}>
          {column.map((cell) => (
            <i key={cell.day} className={`heat-${level(cell.value)}`} title={`${cell.day} — effort ${cell.value}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── First-run ritual (honestly derived from real local activity) ────── */

interface RitualStep {
  id: string;
  title: string;
  hint: string;
  done: boolean;
  href?: string;
}

function buildRitualSteps(signals: { readAtom: boolean; ranLab: boolean; attempted: boolean; keptCard: boolean; focused: boolean }): {
  steps: RitualStep[];
  allDone: boolean;
} {
  const steps: RitualStep[] = [
    { id: 'open', title: 'Open CP Ultimate', hint: 'You are here. That was step one.', done: true },
    { id: 'read', title: 'Read one atom', hint: 'The corpus is atomic — one idea per page.', done: signals.readAtom, href: '/atlas/m13' },
    { id: 'lab', title: 'Run one trace lab', hint: 'Operate an algorithm with your own hands.', done: signals.ranLab, href: '/learn/m13/window-invariant' },
    { id: 'attempt', title: 'Attempt before you peek', hint: 'A wrong guess primes real learning.', done: signals.attempted, href: '/atlas/m13/sets/13.00' },
    { id: 'keep', title: 'Keep one card, return tomorrow', hint: 'Save a check question; spaced repetition does the rest.', done: signals.keptCard, href: '/atlas/m13/atoms/13.00.01' }
  ];
  return { steps, allDone: steps.every((step) => step.done) };
}
